import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { shadows } from '../../design-system/tokens/shadows';
import { animations } from '../../design-system/tokens/animations';

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const Label = styled.label`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  white-space: nowrap;
`;

const Select = styled.select`
  padding: ${spacing[2]} ${spacing[4]};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.focus(props.theme.colors.primary)};
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

export type GroupingOption = {
  value: string;
  label: string;
};

export interface GroupingSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: GroupingOption[];
  label?: string;
  className?: string;
}

export const GroupingSelector: React.FC<GroupingSelectorProps> = ({
  value,
  onChange,
  options,
  label,
  className,
}) => {
  const { t } = useTranslation();
  const resolvedLabel: string = label ?? ((t('grouping.groupBy') as string) || 'Group by:');
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <SelectContainer className={className}>
      {resolvedLabel && <Label>{resolvedLabel}</Label>}
      <Select value={value} onChange={handleChange} aria-label={resolvedLabel}>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </SelectContainer>
  );
};

export default GroupingSelector;
